import { type ReactNode, useId } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { STAGES, type StageSlug, stageStep } from "@/components/agents/configuration-server/stages/stageOrder";
import { cn } from "@/lib/utils";

export function StageHeader({
  number, title, centralHref, live = false, actions, tabs, children,
}: {
  number: "01" | "02" | "03";
  title: string;
  centralHref: string;
  live?: boolean;
  actions?: ReactNode;
  /** Abas coladas na base do hero (Gerenciamento). */
  tabs?: ReactNode;
  children?: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <section className={cn("bg-[var(--exed-navy)] px-8 pt-6 text-white", tabs ? "pb-0" : "pb-8")}>
      <div className={cn("mx-auto flex flex-col gap-5", tabs ? "max-w-[1440px]" : "max-w-[1280px]")}>
        <Link
          to={centralHref}
          className="inline-flex items-center gap-1.5 self-start rounded text-[13px] font-medium text-white/60 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
        >
          <ArrowLeft aria-hidden="true" className="h-3.5 w-3.5" />
          {t("Configuration Server")}
        </Link>
        <div className="flex flex-wrap items-start justify-between gap-6">
          <div className="flex min-w-0 items-start gap-4">
            <div
              data-testid="stage-glyph"
              className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[var(--exed-gold)] font-mono text-base font-semibold text-[var(--exed-ink-900)] shadow-[0_0_0_4px_rgba(255,200,61,0.15)]"
            >
              {number}
            </div>
            <div className="flex min-w-0 flex-col gap-1.5">
              <span className="inline-flex items-center gap-2 font-mono text-[11px] font-medium uppercase tracking-[0.12em] text-[var(--exed-gold)]">
                {live && <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-[var(--exed-gold)] motion-safe:animate-pulse" />}
                {t("Stage {{number}} of 03", { number })}
              </span>
              <h1 className="m-0 break-words text-2xl font-semibold tracking-[-0.01em] text-white">{title}</h1>
              {children}
            </div>
          </div>
          {actions && <div className="flex flex-wrap items-center gap-1.5">{actions}</div>}
        </div>
        {tabs && <div className="-mt-1">{tabs}</div>}
      </div>
    </section>
  );
}

/**
 * Texto com o número inicial em destaque ("**12** pending in …"), como no design.
 * Funciona nas três línguas porque todas começam a frase pelo número.
 */
export function LeadNumber({ text, lead, strongClassName }: { text: string; lead: string; strongClassName?: string }) {
  if (!text.startsWith(lead)) return <>{text}</>;
  return <><strong className={cn("font-semibold", strongClassName)}>{lead}</strong>{text.slice(lead.length)}</>;
}

/** Rodapé fixo das telas de etapa: cola no fim da janela (compensa o `p-6` do `main`). */
export const STAGE_FOOTER_CLASS =
  "sticky bottom-[-1.5rem] z-10 mt-auto border-t border-[var(--exed-card-border)] bg-white px-6 py-3";

export function StageFooter({
  children, testId = "stage-footer", footerRef,
}: {
  children: ReactNode;
  testId?: string;
  /** Callback ref: quem precisa saber onde o rodapé está (o painel que cabe acima dele). */
  footerRef?: (element: HTMLDivElement | null) => void;
}) {
  return (
    <div ref={footerRef} data-testid={testId} className={STAGE_FOOTER_CLASS}>
      <div className="mx-auto flex max-w-[1440px] flex-wrap items-center justify-between gap-4">{children}</div>
    </div>
  );
}

/**
 * Os dois botões do rodapé: voltar e avançar entre os estágios Extração, Gerenciamento e
 * Execução. Nas pontas o botão fica desabilitado. `nextReason` trava o Próximo e mostra o
 * motivo; `nextDisabled` trava sem motivo. O Voltar nunca trava.
 *
 * A cor segue o que dá para clicar: o botão clicável é o primário (amarelo) e o desabilitado é
 * branco. Com os dois livres, o Próximo é o amarelo e o Voltar é o branco; sem Próximo, o Voltar
 * passa a ser o amarelo.
 */
export function StageNavButtons({
  stage, centralHref, nextDisabled = false, nextReason,
}: {
  stage: StageSlug;
  centralHref: string;
  nextDisabled?: boolean;
  nextReason?: string;
}) {
  const { t } = useTranslation();
  const reasonId = useId();
  const index = stageStep(stage) - 1;
  const previous = STAGES[index - 1] ?? null;
  const following = STAGES[index + 1] ?? null;
  const locked = nextDisabled || !!nextReason || !following;
  // Só o Voltar clica (último estágio ou Próximo travado): ele é o primário.
  const backIsPrimary = !!previous && locked;
  const nextLabel = following ? t("Next: {{stage}}", { stage: t(following.label) }) : t("Next");
  return (
    <div className="flex flex-wrap items-center justify-end gap-3">
      {nextReason && <span id={reasonId} className="text-[13px] text-[var(--exed-ink-500)]">{nextReason}</span>}
      {previous ? (
        <Button asChild variant={backIsPrimary ? "action" : "outline"} className="h-10 gap-2">
          <Link to={`${centralHref}/${previous.slug}`}>
            <ArrowLeft aria-hidden="true" className="h-4 w-4" />
            {t("Back: {{stage}}", { stage: t(previous.label) })}
          </Link>
        </Button>
      ) : (
        <Button disabled variant="outline" className="h-10 gap-2">
          <ArrowLeft aria-hidden="true" className="h-4 w-4" />
          {t("Back")}
        </Button>
      )}
      {locked || !following ? (
        <Button
          disabled aria-describedby={nextReason ? reasonId : undefined} variant="outline"
          className="h-10 gap-2 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {nextLabel}
          <ArrowRight aria-hidden="true" className="h-4 w-4" />
        </Button>
      ) : (
        <Button asChild variant="action" className="h-10 gap-2">
          <Link to={`${centralHref}/${following.slug}`}>
            {nextLabel}
            <ArrowRight aria-hidden="true" className="h-4 w-4" />
          </Link>
        </Button>
      )}
    </div>
  );
}
